/**
 * Help schemas shared by the engine, the skill and the page (epic
 * PI-SPEC-SW-E302, ER-1655, contract help 1).
 *
 * One help turn travels like this:
 *
 * 1. The page calls `spectreeToolbox.help.ask` with the question, up to
 *    {@link HELP_IMAGES_MAX} screenshots and the page context
 *    ({@link helpAskInputSchema}).
 * 2. The engine writes the screenshots and `turn.json`
 *    ({@link helpTurnFileSchema}) into the conversation folder (mode 0600)
 *    and runs the internal `toolbox-help` skill with `claude -p`, read-only.
 * 3. Answer text streams to the page as `answer-delta` events. The skill ends
 *    with one line, `SPECTREE_HELP_TURN {json}` ({@link helpTurnMarkerSchema}),
 *    which the engine strips from the text and narrows with
 *    {@link normalizeHelpTurnMarker}: sources, in-app links and confirm
 *    buttons that are not valid are dropped, never refused, so one bad entry
 *    never loses a whole answer.
 * 4. `turn-finished` carries the answer, verdict, sources, links and actions.
 *
 * Images never touch the SpecTree API during a turn; only a confirmed
 * "Send to Aaron" uploads them to the Help inbox (`api.ts`).
 */
import { z } from "zod";
import { absoluteFilePathSchema } from "../formulator/api.js";
import { boundedRecord } from "../zod-helpers.js";
import { HELP_ACTIONS, HELP_ACTION_LABELS, HELP_ANSWER_ACTIONS_MAX, HELP_IMAGE_BYTES_MAX, HELP_IMAGE_MIME_TYPES, HELP_IMAGE_NAME_MAX, HELP_IMAGES_MAX, HELP_LABEL_MAX, HELP_LINK_PATH_MAX, HELP_LINKS_MAX, HELP_MESSAGE_MAX, HELP_MESSAGE_ROLES, HELP_PAGE_TITLE_MAX, HELP_QUESTION_MAX, HELP_SCREEN_KEYS, HELP_SOURCE_KINDS, HELP_SOURCE_REF_MAX, HELP_SOURCE_TITLE_MAX, HELP_SOURCES_MAX, HELP_STATUS_STATES, HELP_SUGGESTIONS_PER_SCREEN_MAX, HELP_TURN_INPUT_SCHEMA_VERSION, HELP_TURN_MARKER, HELP_TURN_MESSAGES_MAX, HELP_VERDICTS, isHelpLinkPath, } from "./constants.js";
// ---------------------------------------------------------------------------
// Page context
// ---------------------------------------------------------------------------
/**
 * What the page sends with every question, so "what am I looking at?" works
 * without a screenshot: the route, page title, the person's Toolbox roles on
 * the project (empty when they have none yet), the project and item on
 * screen, and the versions (app, SpecTree server, help pack).
 */
export const helpContextSchema = z
    .object({
    route: z.string().startsWith("/").max(HELP_LINK_PATH_MAX),
    pageTitle: z.string().max(HELP_PAGE_TITLE_MAX),
    roles: z.array(z.string().min(1).max(40)).max(12),
    project: z.string().trim().min(1).max(256).optional(),
    itemId: z.string().trim().min(1).max(64).optional(),
    appVersion: z.string().min(1).max(64),
    spectreeVersion: z.string().min(1).max(64).optional(),
    packVersion: z.string().min(1).max(64).optional(),
})
    .strict();
/** One message of a Help conversation, as `turn.json` and `help.ask` carry it. */
export const helpMessageSchema = z
    .object({
    role: z.enum(HELP_MESSAGE_ROLES),
    text: z.string().max(HELP_MESSAGE_MAX),
})
    .strict();
/** The person's question: 1-{@link HELP_QUESTION_MAX} characters, not blank. */
export const helpQuestionSchema = z
    .string()
    .min(1, "question must not be empty")
    .max(HELP_QUESTION_MAX, `question must be at most ${String(HELP_QUESTION_MAX)} characters`)
    .refine((value) => value.trim().length > 0, "question must not be blank");
// ---------------------------------------------------------------------------
// Screenshots
// ---------------------------------------------------------------------------
const BASE64_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/;
/**
 * The decoded size, in bytes, of a base64 string (no line breaks). Returns
 * -1 when `dataBase64` is not valid base64. Browser-safe: no `Buffer`.
 */
export function helpImageDecodedBytes(dataBase64) {
    if (dataBase64.length % 4 !== 0 || !BASE64_PATTERN.test(dataBase64))
        return -1;
    const padding = dataBase64.endsWith("==") ? 2 : dataBase64.endsWith("=") ? 1 : 0;
    return (dataBase64.length / 4) * 3 - padding;
}
/** True when a decoded size is a non-empty image within {@link HELP_IMAGE_BYTES_MAX}. */
export function isHelpImageSizeAllowed(bytes) {
    return Number.isInteger(bytes) && bytes > 0 && bytes <= HELP_IMAGE_BYTES_MAX;
}
/**
 * One screenshot as the page hands it to `help.ask` (pasted, dropped, picked
 * or captured with "Attach this screen"): a file name, an accepted image type
 * and the bytes as base64. The decoded size must be 1 byte to
 * {@link HELP_IMAGE_BYTES_MAX}.
 */
export const helpImageInputSchema = z
    .object({
    name: z.string().trim().min(1).max(HELP_IMAGE_NAME_MAX),
    mimeType: z.enum(HELP_IMAGE_MIME_TYPES),
    dataBase64: z.string().min(1),
})
    .strict()
    .superRefine((image, ctx) => {
    const bytes = helpImageDecodedBytes(image.dataBase64);
    if (bytes < 0) {
        ctx.addIssue({ code: "custom", path: ["dataBase64"], message: "dataBase64 must be base64" });
    }
    else if (!isHelpImageSizeAllowed(bytes)) {
        ctx.addIssue({
            code: "custom",
            path: ["dataBase64"],
            message: `an image must be 1 byte to ${String(HELP_IMAGE_BYTES_MAX)} bytes`,
        });
    }
});
// ---------------------------------------------------------------------------
// Ask (page -> engine)
// ---------------------------------------------------------------------------
/**
 * What `help.ask` takes. `conversationId` is a UUID the page makes when the
 * panel opens a new conversation and keeps until it is ended. `messages` is
 * the conversation so far as the panel shows it; the engine keeps the last
 * {@link HELP_TURN_MESSAGES_MAX} in `turn.json`.
 */
export const helpAskInputSchema = z
    .object({
    conversationId: z.guid("conversationId must be a UUID"),
    question: helpQuestionSchema,
    images: z.array(helpImageInputSchema).max(HELP_IMAGES_MAX, `at most ${String(HELP_IMAGES_MAX)} images`),
    context: helpContextSchema,
    messages: z.array(helpMessageSchema).max(200).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// turn.json (engine -> skill)
// ---------------------------------------------------------------------------
/**
 * `turn.json`: what the engine hands the `toolbox-help` skill for one turn.
 * Written (mode 0600) in the conversation folder; person text goes here,
 * never on the command line. `images` are absolute paths of the screenshots
 * the engine wrote next to it, which the skill `Read`s.
 */
export const helpTurnFileSchema = z
    .object({
    schema: z.literal(HELP_TURN_INPUT_SCHEMA_VERSION),
    conversationId: z.guid("conversationId must be a UUID"),
    messages: z.array(helpMessageSchema).max(HELP_TURN_MESSAGES_MAX),
    question: helpQuestionSchema,
    images: z.array(absoluteFilePathSchema).max(HELP_IMAGES_MAX),
    context: helpContextSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Sources, links and confirm buttons
// ---------------------------------------------------------------------------
/** What an answer relied on: a guide section, a screen, code or another doc. */
export const helpSourceSchema = z.object({
    kind: z.enum(HELP_SOURCE_KINDS),
    title: z.string().trim().min(1).max(HELP_SOURCE_TITLE_MAX),
    ref: z.string().trim().min(1).max(HELP_SOURCE_REF_MAX).optional(),
});
/** An in-app link ("Open Setup"). `path` must pass {@link isHelpLinkPath}. */
export const helpLinkSchema = z.object({
    label: z.string().trim().min(1).max(HELP_LABEL_MAX),
    path: z.string().refine(isHelpLinkPath, { message: "path must be an in-app Toolbox path" }),
});
const helpEntryIdSchema = z.string().trim().min(1).max(64);
const helpProjectSchema = z.string().trim().min(1).max(256);
/** The arguments each action on the fixed list takes. Unknown keys are refused. */
export const helpActionArgsSchemas = {
    "retry-queue-entry": z.object({ entryId: helpEntryIdSchema }).strict(),
    "resume-queue": z.object({}).strict(),
    "update-repo-copies": z.object({ project: helpProjectSchema }).strict(),
    "sign-in-claude": z.object({}).strict(),
    "sign-in-repos": z.object({ project: helpProjectSchema }).strict(),
};
/**
 * A confirm button: an action from the fixed list, its label and its
 * arguments. The page performs it only after the person clicks.
 */
export const helpActionSchema = z
    .object({
    action: z.enum(HELP_ACTIONS),
    label: z.string().trim().min(1).max(HELP_LABEL_MAX),
    args: z.record(z.string(), z.unknown()),
})
    .superRefine((entry, ctx) => {
    if (!helpActionArgsSchemas[entry.action].safeParse(entry.args).success) {
        ctx.addIssue({ code: "custom", path: ["args"], message: `invalid arguments for ${entry.action}` });
    }
});
/**
 * Keep only the confirm buttons the page may show: an action on
 * {@link HELP_ACTIONS} with valid arguments. A missing or blank label gets
 * the default from {@link HELP_ACTION_LABELS}. Anything else is dropped, never
 * refused. At most {@link HELP_ANSWER_ACTIONS_MAX} are kept; duplicates (same
 * action and arguments) keep the first.
 */
export function filterHelpActions(markerActions) {
    const kept = [];
    const seen = new Set();
    for (const raw of markerActions) {
        if (raw === null || typeof raw !== "object" || Array.isArray(raw))
            continue;
        const candidate = raw;
        const action = candidate.action;
        if (typeof action !== "string" || !HELP_ACTIONS.includes(action))
            continue;
        const id = action;
        const label = typeof candidate.label === "string" && candidate.label.trim().length > 0
            ? candidate.label
            : HELP_ACTION_LABELS[id];
        const parsed = helpActionSchema.safeParse({ action: id, label, args: candidate.args ?? {} });
        if (!parsed.success)
            continue;
        const key = JSON.stringify([parsed.data.action, parsed.data.args]);
        if (seen.has(key))
            continue;
        seen.add(key);
        kept.push(parsed.data);
        if (kept.length >= HELP_ANSWER_ACTIONS_MAX)
            break;
    }
    return kept;
}
/** Keep only valid in-app links (at most {@link HELP_LINKS_MAX}); the rest are dropped. */
export function filterHelpLinks(markerLinks) {
    const kept = [];
    for (const raw of markerLinks) {
        const parsed = helpLinkSchema.safeParse(raw);
        if (!parsed.success)
            continue;
        kept.push(parsed.data);
        if (kept.length >= HELP_LINKS_MAX)
            break;
    }
    return kept;
}
/** Keep only valid sources (at most {@link HELP_SOURCES_MAX}); the rest are dropped. */
export function filterHelpSources(markerSources) {
    const kept = [];
    for (const raw of markerSources) {
        const parsed = helpSourceSchema.safeParse(raw);
        if (!parsed.success)
            continue;
        kept.push(parsed.data);
        if (kept.length >= HELP_SOURCES_MAX)
            break;
    }
    return kept;
}
// ---------------------------------------------------------------------------
// The marker line (skill -> engine)
// ---------------------------------------------------------------------------
/**
 * The JSON after `SPECTREE_HELP_TURN ` on the skill's last line. Only
 * `conversationId` is required to match; the engine refuses a marker for
 * another conversation (`NO_TURN_RETURNED`). `sources`, `links` and `actions`
 * are arrays of anything here and are narrowed by
 * {@link normalizeHelpTurnMarker}; an unknown `verdict` reads as `not-sure`.
 */
export const helpTurnMarkerSchema = z.object({
    conversationId: z.guid("conversationId must be a UUID"),
    verdict: z.enum(HELP_VERDICTS).catch("not-sure"),
    sources: z.array(z.unknown()).catch([]),
    links: z.array(z.unknown()).catch([]),
    actions: z.array(z.unknown()).catch([]),
});
/** Narrow a parsed marker to the parts the page may show. */
export function normalizeHelpTurnMarker(marker) {
    return {
        conversationId: marker.conversationId,
        verdict: marker.verdict,
        sources: filterHelpSources(marker.sources),
        links: filterHelpLinks(marker.links),
        actions: filterHelpActions(marker.actions),
    };
}
/**
 * Find the last `SPECTREE_HELP_TURN {json}` line in `text`, parse it and
 * narrow it. Returns null when there is no valid marker line.
 */
export function parseHelpTurnMarkerLine(text) {
    const lines = text.split(/\r?\n/);
    for (let i = lines.length - 1; i >= 0; i -= 1) {
        const line = (lines[i] ?? "").trim();
        if (!line.startsWith(`${HELP_TURN_MARKER} `) && line !== HELP_TURN_MARKER)
            continue;
        let json;
        try {
            json = JSON.parse(line.slice(HELP_TURN_MARKER.length).trim());
        }
        catch {
            return null;
        }
        const parsed = helpTurnMarkerSchema.safeParse(json);
        return parsed.success ? normalizeHelpTurnMarker(parsed.data) : null;
    }
    return null;
}
/**
 * The answer text with every marker line removed and trailing blank lines
 * trimmed, so the marker never reaches the panel.
 */
export function stripHelpTurnMarker(text) {
    return text
        .split(/\r?\n/)
        .filter((line) => !line.trimStart().startsWith(HELP_TURN_MARKER))
        .join("\n")
        .trimEnd();
}
// ---------------------------------------------------------------------------
// Engine status and the help pack
// ---------------------------------------------------------------------------
/**
 * Readiness of the help engine. `resetsAt` (ISO 8601) is set for
 * `usage-limit`; `message` is one plain sentence for the person. Lenient, so
 * a newer app may add fields.
 */
export const helpStatusSchema = z
    .object({
    state: z.enum(HELP_STATUS_STATES),
    resetsAt: z.string().optional(),
    message: z.string(),
})
    .loose();
/** One guide in the bundled help pack, for the not-ready state and links. */
export const helpGuideSummarySchema = z.object({
    id: z.string().min(1).max(100),
    title: z.string().min(1).max(HELP_SOURCE_TITLE_MAX),
});
/**
 * What `help.getPackInfo()` resolves to: the SpecTree version the pack
 * describes (compare with the server's to say "my notes may be out of
 * date"), the app version, its guides, and suggested questions per screen
 * key ({@link HELP_SCREEN_KEYS}; unknown keys from a newer pack are kept).
 */
export const helpPackInfoSchema = z
    .object({
    spectreeVersion: z.string().min(1),
    toolboxVersion: z.string().min(1),
    guides: z.array(helpGuideSummarySchema).max(50),
    suggestions: boundedRecord(z.string().min(1).max(40), z.array(z.string().min(1).max(HELP_QUESTION_MAX)).max(HELP_SUGGESTIONS_PER_SCREEN_MAX), HELP_SCREEN_KEYS.length * 4),
})
    .loose();
/** One guide's text, as `help.getGuide(id)` returns it (markdown). */
export const helpGuideSchema = helpGuideSummarySchema.extend({ markdown: z.string() });
//# sourceMappingURL=types.js.map